import {
  DEFAULT_EDGE_PRODUCTS,
  defaultEdgeRemovalPartNoFromTargetRef,
  ExportPreviewViewSchema,
  shouldInjectDefaultEdgeRows,
  type DefaultEdgeProduct,
  type ExportPreviewView,
  type ScheduleEntry,
  type SourceRef
} from "@automatry/schemas";

import { humaniseCatalogItemText, solutionDisplayName } from "../app/format";
import type {
  ProjectFixtureControlsBomRow,
  ProjectFixtureForDisplay,
  ProjectFixtureMappingScheduleRow,
  ProjectFixtureOutputDeviceInstance,
  ProjectFixtureOutputGraph,
  ProjectFixtureOutputSupplySource
} from "./projectCockpitAdapter";
import { isCablingDescriptor } from "./spec/compareVM";

export type ExportIncludeKey =
  | "takeoffs"
  | "bom"
  | "apartmentSchedules"
  | "schedules"
  | "notes"
  | "requirements"
  | "rfi"
  | "cabling"
  | "details"
  | "install"
  | "commissioning"
  | "edge";

export type ExportScopeKind = "job" | "apartmentType";
export type ExportBomFilter =
  | "all"
  | "titan-supplied"
  | "in-house"
  | "fabricated"
  | "third-party";

export interface ExportScopeOption {
  id: string;
  label: string;
  kind: ExportScopeKind;
  quantity: number;
}

export interface ExportPreviewBomRowDetail {
  id: string;
  apartmentTypeId: string;
  apartmentTypeLabel: string;
  provisionScope?: "apartment_type" | "project";
  installIncluded?: boolean;
  provisionScopeBasis?: string;
  installIncludedBasis?: string;
  quantityPerApartmentType: number;
  apartmentTypeQuantity: number;
  projectQuantity: number;
  installNotes: string[];
  sourceRefs: SourceRef[];
  sourceRefCount: number;
  ruleTraceRefs: string[];
  riskRefs: string[];
  specItemRefs: string[];
}

export interface ExportPreviewBomRowDisplay {
  id: string;
  apartmentTypeId: string;
  apartmentTypeLabel: string;
  apartmentTypeIds: string[];
  apartmentTypeLabels: string[];
  itemClass: ProjectFixtureControlsBomRow["itemClass"];
  itemKind: string;
  itemKindLabel: string;
  productPartNo?: string;
  name: string;
  role: string;
  description: string;
  supplySource: ProjectFixtureOutputSupplySource;
  supplySourceGroup: Exclude<ExportBomFilter, "all">;
  supplySourceLabel:
    | "Titan-supplied"
    | "Automatry Edge"
    | "Fabricated"
    | "Third-party"
    | "By others";
  provisionScope?: "apartment_type" | "project";
  installIncluded?: boolean;
  provisionScopeBasis?: string;
  installIncludedBasis?: string;
  quantityPerApartmentType: number;
  apartmentTypeQuantity: number;
  projectQuantity: number;
  why: string;
  how: string;
  defaultApplied?: boolean;
  provenanceWhy?: string;
  provenanceWhere?: string;
  installNotes: string[];
  sourceRefs: SourceRef[];
  sourceRefCount: number;
  ruleTraceRefs: string[];
  riskRefs: string[];
  specItemRefs: string[];
  sourceRowCount: number;
  detailRows: ExportPreviewBomRowDetail[];
}

export interface ExportPreviewRequirementRow {
  id: string;
  text: string;
  kindLabel: string;
  statusLabel: string;
  confidence: number;
  confidenceLabel: string;
  sourceRefs: SourceRef[];
  sourceRefCount: number;
}

export interface ExportPreviewInstallNote {
  id: string;
  itemLabel: string;
  apartmentTypeId: string;
  apartmentTypeLabel: string;
  note: string;
  why: string;
  riskRefs: string[];
}

export interface ExportPreviewTakeoffRow {
  id: string;
  apartmentTypeId: string;
  apartmentTypeLabel: string;
  quantity: number;
  bedsLabel: string;
  fcuCountLabel: string;
  aiWhat: string;
  aiWhy: string;
  traceability: string;
  sourceRefCount: number;
}

export interface ExportPreviewApartmentScheduleRow {
  id: string;
  apartmentTypeId: string;
  apartmentTypeLabel: string;
  homes: number;
  controllersPerHome: number;
  devicesPerHome: number;
  infrastructurePerHome: number;
  featuredProducts: string[];
  aiWhat: string;
  aiWhy: string;
  sourceRefCount: number;
  ruleTraceRefCount: number;
}

export interface ExportPreviewRfiRow {
  id: string;
  title: string;
  question: string;
  affectedLabel: string;
  priorityLabel: string;
  aiWhy: string;
  traceability: string;
}

export interface ExportPreviewCablingRow {
  id: string;
  apartmentTypeId: string;
  apartmentTypeLabel: string;
  method: string;
  appliesTo: string;
  quantity: number;
  protocolLabel: string;
  aiWhy: string;
  sourceRefCount: number;
  ruleTraceRefCount: number;
}

export interface ExportPreviewDetailRow {
  id: string;
  apartmentTypeId?: string;
  title: string;
  detail: string;
  affectedLabel: string;
  categoryLabel: string;
  aiWhy: string;
  sourceRefCount: number;
  ruleTraceRefCount: number;
}

export interface ExportPreviewSpecificationSection {
  id: string;
  heading: string;
  body: string;
  aiWhy: string;
  sourceRefCount: number;
}

export interface ExportPreviewDeferredScheduleExtensionPoint {
  id: "clarifications-exclusions-assumptions" | "notes-client-schedule";
  label: string;
  followUp: string;
  reason: string;
}

export interface ExportPreviewScheduleEntry {
  id: string;
  kind: ScheduleEntry["kind"];
  kindLabel: string;
  categoryLabel: string;
  severityLabel: string;
  affectedApartmentTypeIds: string[];
  affectedLabel: string;
  bodyText: string;
  provenanceLabel: string;
  evidenceSummary: string;
  sourceRefs: SourceRef[];
  sourceRefCount: number;
  ruleTraceRefs: string[];
  ruleTraceRefCount: number;
  sourceNoteId?: string;
  sourceFlagId?: string;
  sourceFlagFamilyId?: string;
}

export interface ExportPreviewNoteRow {
  id: string;
  kindLabel: string;
  subjectLabel: string;
  text: string;
  authorLabel: string;
  statusLabel: string;
  provenanceLabel: string;
  evidenceSummary: string;
  promotedRequirementId?: string;
  conflictsWith: string[];
  createdAt: string;
  updatedAt?: string;
}

export interface ExportPreviewMappingRow {
  id: string;
  apartmentTypeId: string;
  apartmentTypeLabel: string;
  deviceLabel: string;
  controllerLabel?: string;
  terminationLabel?: string;
  roomLabel: string;
  cableImplication?: string;
  commissioningNote: string;
  riskRefs: string[];
  sourceRefCount: number;
  sourceRefs: SourceRef[];
  ruleTraceRefs: string[];
}

export interface ExportPreviewEdgeExtension {
  id: string;
  title: string;
  apartmentTypeLabels: string[];
  provisionUnit: string;
  inputPolicy: string;
  connectivity: string;
  installNotes: string[];
  needsUserDecision: boolean;
  bomRowIds: string[];
  defaultApplied?: boolean;
  capabilities: string[];
  provenanceWhy: string;
  provenanceWhere: string;
}

export interface ExportPreviewStats {
  bomLineCount: number;
  totalControlsQuantity: number;
  takeoffRowCount: number;
  apartmentScheduleCount: number;
  rfiCount: number;
  cablingRowCount: number;
  detailCount: number;
  specificationSectionCount: number;
  scheduleEntryCount: number;
  noteCount: number;
  requirementCount: number;
  installNoteCount: number;
  mappingRowCount: number;
  commissioningTaskCount: number;
  edgeExtensionCount: number;
}

export interface ExportPreviewDisplayViewModel extends ExportPreviewView {
  generatedFrom: string;
  partialScope: ProjectFixtureForDisplay["fixture"]["partialScope"] | null;
  /**
   * Track 1: present when the specification contains a NOT-CERTIFIED draft
   * apartment type. Export/issue must stay blocked while it is present.
   */
  draftScope: ProjectFixtureForDisplay["fixture"]["draftScope"] | null;
  scopeOptions: ExportScopeOption[];
  includeOptions: { id: ExportIncludeKey; label: string; defaultChecked: boolean }[];
  bomRows: ExportPreviewBomRowDisplay[];
  requirements: ExportPreviewRequirementRow[];
  installNotes: ExportPreviewInstallNote[];
  takeoffRows: ExportPreviewTakeoffRow[];
  apartmentSchedules: ExportPreviewApartmentScheduleRow[];
  rfiRows: ExportPreviewRfiRow[];
  cablingRows: ExportPreviewCablingRow[];
  detailRows: ExportPreviewDetailRow[];
  specificationSections: ExportPreviewSpecificationSection[];
  scheduleEntries: ExportPreviewScheduleEntry[];
  noteRows: ExportPreviewNoteRow[];
  deferredScheduleExtensionPoints: ExportPreviewDeferredScheduleExtensionPoint[];
  mappingRows: ExportPreviewMappingRow[];
  edgeExtensions: ExportPreviewEdgeExtension[];
  stats: ExportPreviewStats;
  supplySourceBreakdown: Record<
    Exclude<ExportBomFilter, "all">,
    { label: string; rowCount: number; quantity: number }
  >;
  outputBundleHash: string;
  openRiskIds: string[];
  dataGaps: string[];
}

const includeOptions: ExportPreviewDisplayViewModel["includeOptions"] = [
  { id: "takeoffs", label: "Take-offs", defaultChecked: true },
  { id: "bom", label: "Products", defaultChecked: true },
  { id: "apartmentSchedules", label: "Apartment schedules", defaultChecked: true },
  { id: "schedules", label: "Clarifications / exclusions", defaultChecked: true },
  { id: "requirements", label: "Specifications", defaultChecked: true },
  { id: "cabling", label: "Cabling", defaultChecked: true },
  { id: "details", label: "Details", defaultChecked: true },
  { id: "edge", label: "Edge extensions", defaultChecked: true }
];

const labelise = (value: string): string =>
  value
    .replace(/^carrington-/, "")
    .replace(/^type-/, "Type ")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

type ClientDeliverableProductCodePolicy = "redact" | "preserve";

const clientDeliverableProductCodePolicy = (): ClientDeliverableProductCodePolicy =>
  "preserve";

export const humaniseExportText = (value: string): string =>
  value
    .replace(
      /\bController is required by deterministic overflow packing\./gi,
      "Controller is required to keep each controller within capacity."
    )
    .replace(/\bdeterministic overflow packing\b/gi, "controller-capacity packing")
    .replace(/\bthe deterministic engine\b/gi, "the specification engine")
    .replace(/\bdeterministic\b/gi, "repeatable")
    .replace(
      /;\s*Lane B must derive the required sensors, gateway\/interface, and notifications from this requirement\./gi,
      ", including the self-contained gateway PC, dashboard path, alert workflow, and any required leak-sensor confirmation."
    )
    .replace(
      /\bLane B must derive the required sensors, gateway\/interface, and notifications from this requirement\./gi,
      "Include the self-contained gateway PC, dashboard path, alert workflow, and any required leak-sensor confirmation."
    )
    .replace(
      /\bM-Bus references are treated as protocol evidence for Lane B\b/gi,
      "M-Bus references are treated as protocol evidence for the controls design"
    )
    .replace(/\bso Lane B must model\b/gi, "so the controls design should model")
    .replace(/\bLane B\b/gi, "the controls design")
    .replace(/\bFCUs\b/gi, "fan-coil units")
    .replace(/\bFCU\b/gi, "fan-coil unit")
    .replace(/\bbuilding\s+BMS\b/gi, "building management system")
    .replace(/\bBMS\b/gi, "building management system")
    .replace(/\bWUFH\b/gi, "wet underfloor heating")
    .replace(/\bEUFH\b/gi, "electric underfloor heating")
    .replace(/\bUFH\b/gi, "underfloor heating")
    .replace(/\s+/g, " ")
    .trim();

const humaniseExportDisplayText = (value: string | null | undefined): string =>
  humaniseExportText(humaniseCatalogItemText(value));

const stripProductPartNos = (value: string, partNos: readonly string[] = []): string =>
  partNos.reduce((current, partNo) => {
    const cleanedPartNo = partNo.trim();

    if (!cleanedPartNo) {
      return current;
    }

    return current.replace(
      new RegExp(`\\b${escapeRegExp(cleanedPartNo)}\\b`, "gi"),
      ""
    );
  }, value);

const internalClientRefReplacements: readonly [RegExp, string][] = [
  [/\bcontroller-[a-z0-9][a-z0-9_-]*\b/gi, "the controller"],
  [/\bzone-[a-z0-9][a-z0-9_-]*\b/gi, "the served zone"],
  [/\b(?:device-map-)?device-[a-z0-9][a-z0-9_-]*\b/gi, "the device"],
  [/\binstance-[a-z0-9][a-z0-9_-]*\b/gi, "the device"],
  [/\bpoint-demand-[a-z0-9][a-z0-9_-]*\b/gi, "the point demand"],
  [/\bphysical-[a-z0-9][a-z0-9_-]*\b/gi, "the termination"],
  [/\blogical-[a-z0-9][a-z0-9_-]*\b/gi, "the logical point"],
  [/\bmapping-row-[a-z0-9][a-z0-9_-]*\b/gi, "the mapping row"],
  [/\btrace-[a-z0-9][a-z0-9_-]*\b/gi, "the retained check"],
  [
    /\b(?:proposal|loop-step|tool-call|tool-request|verifier-tool|snapshot|run)-[a-z0-9][a-z0-9_-]*\b/gi,
    "the workflow record"
  ],
  [/\breq_[a-z0-9_]+\b/gi, "the requirement"],
  [/\brisk-[a-z0-9][a-z0-9_-]*\b/gi, "the risk"]
];

const scrubInternalClientRefs = (value: string): string =>
  internalClientRefReplacements
    .reduce(
      (current, [pattern, replacement]) => current.replace(pattern, replacement),
      value
    )
    .replace(/\s+([,.;:])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

const withClientProductCode = (
  value: string,
  partNo: string | null | undefined
): string => {
  const cleanedPartNo = partNo?.trim();

  if (!cleanedPartNo || clientDeliverableProductCodePolicy() !== "preserve") {
    return value;
  }

  if (new RegExp(`\\b${escapeRegExp(cleanedPartNo)}\\b`, "i").test(value)) {
    return value;
  }

  return `${value} (${cleanedPartNo})`;
};

// Single client-deliverable egress gate. Integration can flip product-code
// policy here; internal refs are always removed before client surfaces render.
const clientDeliverableText = (
  value: string | null | undefined,
  options: { productPartNos?: readonly string[] } = {}
): string => {
  const humanised = humaniseExportDisplayText(scrubInternalClientRefs(value ?? ""));
  const productPolicyApplied =
    clientDeliverableProductCodePolicy() === "redact"
      ? stripProductPartNos(humanised, options.productPartNos)
      : humanised;

  return scrubInternalClientRefs(productPolicyApplied);
};

const clientProductLabel = (value: string, partNo: string | null | undefined): string =>
  clientDeliverableText(withClientProductCode(value, partNo), {
    productPartNos: [partNo ?? ""]
  });

const BMS_METERING_EXPOSURE_PART_NO = "NETMASTER-BACNET-IP-BMS-METERING-EXPOSURE";
const MODBUS_METERING_BUS_PART_NO = "MODBUS-RTU-RS485-DAISY-CHAIN";
const DEFAULT_NO_TRIGGER_LABEL =
  "Default - no specific requirement found in the documents";
const DEFAULT_EDGE_EXPORT_WHY = `${DEFAULT_NO_TRIGGER_LABEL}. Edge is included for every controls project unless removed during review.`;
const DEFAULT_EDGE_EXPORT_WHERE =
  "Where: applied in the export viewmodel; no source trail is emitted for this default row.";
const DEFAULT_EDGE_CAPABILITIES = [
  "Project Edge gateway allowance",
  "BACnet/IP dashboard path",
  "Alert-ready workflow"
];

const defaultEdgeProductForKind = (
  itemKindOrPartNo: string | null | undefined
): DefaultEdgeProduct | undefined => {
  const normalized = itemKindOrPartNo?.trim();

  if (!normalized) {
    return undefined;
  }

  const normalizedKind = normalized.toLowerCase();
  const normalizedPartNo = normalized.toUpperCase();

  return DEFAULT_EDGE_PRODUCTS.find(
    (product) =>
      product.productPartNo === normalizedPartNo || product.itemKind === normalizedKind
  );
};

const defaultEdgeSupplySource = (
  _supplySource: DefaultEdgeProduct["supplySource"]
): ProjectFixtureOutputSupplySource => "in-house";

const defaultEdgeProductName = (product: DefaultEdgeProduct): string => product.role;

const defaultEdgeProductDescription = (product: DefaultEdgeProduct): string => {
  const fallbackRole = product.role;

  switch (product.productPartNo) {
    case "AUTO-EDGE-GATEWAY":
      return "Self-contained Edge gateway PC for dashboard and alert workflows.";
    default:
      return fallbackRole;
  }
};

const defaultEdgeInstallNote = (product: DefaultEdgeProduct): string => {
  const fallbackRole = product.role;

  switch (product.productPartNo) {
    case "AUTO-EDGE-GATEWAY":
      return "Allow power, network uplink, and dashboard/alert setup for the Edge gateway.";
    default:
      return `Include ${fallbackRole}.`;
  }
};

const isBmsMeteringExposureBomRow = (row: ProjectFixtureControlsBomRow): boolean => {
  const descriptor = [
    row.productPartNo,
    row.description,
    row.itemKind,
    row.why,
    row.how
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ")
    .toLowerCase();

  return (
    row.productPartNo === BMS_METERING_EXPOSURE_PART_NO ||
    /bms[_ ]metering[_ ]exposure|bacnet[_ /-]?ip.*metering.*exposure/.test(descriptor)
  );
};

const isModbusMeteringBusBomRow = (row: ProjectFixtureControlsBomRow): boolean => {
  const descriptor = [row.productPartNo, row.description, row.itemKind]
    .filter((value): value is string => Boolean(value))
    .join(" ")
    .toLowerCase();

  return (
    row.productPartNo === MODBUS_METERING_BUS_PART_NO ||
    /modbus[_ /-]?rtu.*rs485|rs-?485.*daisy.?chain|metering.?bus/.test(descriptor)
  );
};

const displayPartNo = (row: ProjectFixtureControlsBomRow): string | undefined => {
  if (isBmsMeteringExposureBomRow(row)) {
    return "BMS-METERING-LINK";
  }

  if (isModbusMeteringBusBomRow(row)) {
    return "RS485-METERING-BUS";
  }

  const edgeProductCode =
    defaultEdgeProductForKind(row.productPartNo)?.productPartNo ??
    defaultEdgeProductForKind(row.itemKind)?.productPartNo;

  return edgeProductCode ?? row.productPartNo;
};

const itemName = (row: ProjectFixtureControlsBomRow): string => {
  const productPartNo = displayPartNo(row);

  if (isBmsMeteringExposureBomRow(row)) {
    return clientProductLabel("BMS metering interface", productPartNo);
  }

  if (isModbusMeteringBusBomRow(row)) {
    return clientProductLabel("Modbus RS-485 metering bus cabling", productPartNo);
  }

  return clientProductLabel(
    humaniseCatalogItemText(row.itemKind) || labelise(row.itemKind),
    productPartNo
  );
};

const itemDescription = (row: ProjectFixtureControlsBomRow): string => {
  const productPartNo = displayPartNo(row);

  if (isBmsMeteringExposureBomRow(row)) {
    return clientProductLabel(
      "Netmaster exposes apartment meter readings to the landlord BMS.",
      productPartNo
    );
  }

  if (isModbusMeteringBusBomRow(row)) {
    return clientProductLabel(
      "Modbus RS-485 daisy-chain cabling for metering devices.",
      productPartNo
    );
  }

  // Raw fixture descriptions can bake the slug and its human phrase into one
  // string, so humanise it before it reaches export/client copy.
  // the kind slug (and collapse the doubled form) before the prose cleanup.
  return clientProductLabel(
    humaniseCatalogItemText(row.itemKind) || row.description,
    productPartNo
  );
};

const itemKindLabel = (row: ProjectFixtureControlsBomRow): string =>
  humaniseExportDisplayText(row.itemKind);

const takeoffRowsByApartmentType = (fixture: ProjectFixtureForDisplay) =>
  new Map(fixture.takeoffRows.map((row) => [row.apartmentTypeId, row]));

const apartmentTypeLabel = (
  fixture: ProjectFixtureForDisplay,
  apartmentTypeId: string,
  takeoffById = takeoffRowsByApartmentType(fixture)
): string => {
  const takeoff = takeoffById.get(apartmentTypeId);

  return solutionDisplayName(apartmentTypeId, {
    projectId: fixture.project.projectId,
    rawLabel: takeoff?.rawLabel ?? null,
    beds: takeoff?.beds ?? null
  });
};

const scheduleKindLabel: Record<ScheduleEntry["kind"], string> = {
  clarification: "Clarification",
  exclusion: "Exclusion",
  assumption: "Assumption"
};

const scheduleCategoryLabel: Record<ScheduleEntry["category"], string> = {
  scope: "Scope",
  commercial: "Commercial",
  technical: "Technical",
  coordination: "Coordination",
  documentation: "Documentation",
  interface: "Interface",
  programme: "Programme"
};

const scheduleSeverityLabel: Record<ScheduleEntry["severity"], string> = {
  critical: "Critical",
  important: "Important",
  minor: "Minor"
};

const scheduleProvenanceLabel = (entry: ScheduleEntry): string => {
  if (entry.provenance.origin === "user-authored") {
    return "User authored";
  }

  if (entry.provenance.origin === "user-edited") {
    return "User edited";
  }

  if (entry.provenance.origin === "agent-generated-user-edited") {
    return entry.provenance.model
      ? `Agent proposed + user edited · ${entry.provenance.model}`
      : "Agent proposed + user edited";
  }

  if (entry.provenance.origin === "engine-derived") {
    return "Engine derived";
  }

  if (entry.provenance.origin === "agent-edited") {
    return "Agent edited";
  }

  return entry.provenance.model
    ? `Agent proposed · ${entry.provenance.model}`
    : "Agent proposed";
};

const scheduleAffectedLabel = (
  fixture: ProjectFixtureForDisplay,
  entry: ScheduleEntry,
  takeoffById = takeoffRowsByApartmentType(fixture)
): string =>
  entry.affectedApartmentTypeIds.length === 0
    ? "Whole job"
    : entry.affectedApartmentTypeIds
        .map((apartmentTypeId) =>
          apartmentTypeLabel(fixture, apartmentTypeId, takeoffById)
        )
        .join(", ");

const scheduleEntries = (
  fixture: ProjectFixtureForDisplay
): ExportPreviewScheduleEntry[] => {
  const takeoffById = takeoffRowsByApartmentType(fixture);

  return (fixture.scheduleEntries ?? [])
    .filter((entry) => entry.status === "accepted")
    .sort(compareScheduleEntries)
    .map((entry) => ({
      id: entry.id,
      kind: entry.kind,
      kindLabel: scheduleKindLabel[entry.kind],
      categoryLabel: scheduleCategoryLabel[entry.category],
      severityLabel: scheduleSeverityLabel[entry.severity],
      affectedApartmentTypeIds: entry.affectedApartmentTypeIds,
      affectedLabel: scheduleAffectedLabel(fixture, entry, takeoffById),
      bodyText: humaniseExportText(entry.bodyText),
      provenanceLabel: scheduleProvenanceLabel(entry),
      evidenceSummary: humaniseExportText(entry.provenance.evidenceSummary),
      sourceRefs: entry.provenance.sourceRefs,
      sourceRefCount: entry.provenance.sourceRefs.length,
      ruleTraceRefs: entry.provenance.ruleTraceRefs,
      ruleTraceRefCount: entry.provenance.ruleTraceRefs.length,
      ...(entry.provenance.sourceNoteId
        ? { sourceNoteId: entry.provenance.sourceNoteId }
        : {}),
      ...(entry.provenance.sourceFlagId
        ? { sourceFlagId: entry.provenance.sourceFlagId }
        : {}),
      ...(entry.provenance.sourceFlagFamilyId
        ? { sourceFlagFamilyId: entry.provenance.sourceFlagFamilyId }
        : {})
    }));
};

const noteKindLabel = (kind: string): string => {
  switch (kind) {
    case "requirement-amendment":
      return "Requirement amendment";
    case "commissioning":
      return "Commissioning";
    case "exclusion":
      return "Exclusion";
    case "clarification":
      return "Clarification";
    default:
      return labelise(kind);
  }
};

const noteStatusLabel = (status: string | undefined): string => {
  switch (status) {
    case "promoted_to_requirement":
      return "Promoted to requirement";
    case "conflict_flagged":
      return "Conflict flagged";
    case "active":
    case undefined:
      return "Active";
    default:
      return labelise(status);
  }
};

const noteProvenanceLabel = (
  note: NonNullable<ProjectFixtureForDisplay["projectNotes"]>[number]
): string => {
  const author = note.authorType === "ai" ? "AI-authored" : "User-authored";
  const edited =
    note.lastEditedBy === "user" && note.authorType === "ai" ? " + user edited" : "";
  const model = note.aiProvenance?.model ? ` · ${note.aiProvenance.model}` : "";

  return `${author}${edited}${model}`;
};

const noteEvidenceSummary = (
  note: NonNullable<ProjectFixtureForDisplay["projectNotes"]>[number]
): string => {
  const evidence = [
    note.aiProvenance?.where,
    note.aiProvenance?.why,
    note.subjectRef ? `Linked to ${note.subjectRef.label}.` : undefined,
    note.promotedRequirementId
      ? `Promoted requirement ${note.promotedRequirementId}.`
      : undefined,
    note.conflictsWith && note.conflictsWith.length > 0
      ? `Conflict refs: ${note.conflictsWith.join(", ")}.`
      : undefined
  ].filter((part): part is string => Boolean(part?.trim()));

  return evidence.length > 0
    ? humaniseExportText(evidence.join(" "))
    : "No source evidence is attached to this note record.";
};

const noteRows = (fixture: ProjectFixtureForDisplay): ExportPreviewNoteRow[] =>
  (fixture.projectNotes ?? [])
    .filter((note) => note.text.trim().length > 0)
    .sort(
      (left, right) =>
        (left.updatedAt ?? left.createdAt).localeCompare(
          right.updatedAt ?? right.createdAt
        ) || left.id.localeCompare(right.id)
    )
    .map((note) => ({
      id: note.id,
      kindLabel: noteKindLabel(note.kind),
      subjectLabel: note.subjectRef?.label ?? "Whole job",
      text: clientDeliverableText(note.text, {
        productPartNos: productPartNosForFixture(fixture)
      }),
      authorLabel: note.authorType === "ai" ? "AI" : "User",
      statusLabel: noteStatusLabel(note.status),
      provenanceLabel: noteProvenanceLabel(note),
      evidenceSummary: noteEvidenceSummary(note),
      ...(note.promotedRequirementId
        ? { promotedRequirementId: note.promotedRequirementId }
        : {}),
      conflictsWith: [...(note.conflictsWith ?? [])],
      createdAt: note.createdAt,
      ...(note.updatedAt ? { updatedAt: note.updatedAt } : {})
    }));

const compareScheduleEntries = (a: ScheduleEntry, b: ScheduleEntry): number => {
  const kindDelta = scheduleKindOrder(a.kind) - scheduleKindOrder(b.kind);

  if (kindDelta !== 0) {
    return kindDelta;
  }

  const severityDelta =
    scheduleSeverityOrder(a.severity) - scheduleSeverityOrder(b.severity);

  if (severityDelta !== 0) {
    return severityDelta;
  }

  return a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);
};

const scheduleKindOrder = (kind: ScheduleEntry["kind"]): number =>
  kind === "clarification" ? 0 : kind === "exclusion" ? 1 : 2;

const scheduleSeverityOrder = (severity: ScheduleEntry["severity"]): number =>
  severity === "critical" ? 0 : severity === "important" ? 1 : 2;

const productPartNosForFixture = (fixture: ProjectFixtureForDisplay): string[] => [
  ...new Set(
    [
      ...fixture.outputBundle.bomRows.flatMap((row) => [
        row.productPartNo,
        displayPartNo(row)
      ]),
      ...fixture.outputBundle.outputGraphs.flatMap((graph) =>
        graph.deviceInstances.map((device) => device.productPartNo)
      )
    ]
      .map((value) => value?.trim())
      .filter((value): value is string => Boolean(value))
  )
];

const cleanInstallNote = (note: string, fixture: ProjectFixtureForDisplay): string =>
  clientDeliverableText(
    note
      .replace(/^(?:device|physical)-map-[^:]+:\s*/i, "")
      .replace(/\bcreated-[a-z0-9]+(?:-[a-z0-9_]+)*\b/gi, (match) =>
        solutionDisplayName(match, { projectId: fixture.project.projectId })
      ),
    { productPartNos: productPartNosForFixture(fixture) }
  );

const cleanBomProse = (value: string, row: ProjectFixtureControlsBomRow): string =>
  clientDeliverableText(value, { productPartNos: [row.productPartNo ?? ""] });

const isCablingBomRow = (row: ProjectFixtureControlsBomRow): boolean => {
  if (row.itemKind.trim().toLowerCase() === "cabling_note") {
    return true;
  }

  const descriptor = [row.itemKind, row.productPartNo]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return descriptor.length > 0 && isCablingDescriptor(descriptor);
};

const isCablingDisplayRow = (row: ExportPreviewBomRowDisplay): boolean => {
  const descriptor = [row.itemKind, row.productPartNo, row.name]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return descriptor.length > 0 && isCablingDescriptor(descriptor);
};

const sourceGroup = (
  source: ProjectFixtureOutputSupplySource
): Exclude<ExportBomFilter, "all"> =>
  source === "fabricated"
    ? "fabricated"
    : source === "in-house"
      ? "in-house"
      : source === "third-party"
        ? "third-party"
        : "titan-supplied";

const sourceLabel = (
  source: ProjectFixtureOutputSupplySource
): ExportPreviewBomRowDisplay["supplySourceLabel"] => {
  const group = sourceGroup(source);

  if (group === "fabricated") {
    return "Fabricated";
  }

  if (group === "in-house") {
    return "Automatry Edge";
  }

  if (group === "third-party") {
    return "Third-party";
  }

  return "Titan-supplied";
};

const sortBomRows = (
  rows: readonly ExportPreviewBomRowDisplay[]
): ExportPreviewBomRowDisplay[] => {
  const sourceOrder: Record<Exclude<ExportBomFilter, "all">, number> = {
    "titan-supplied": 0,
    "in-house": 1,
    fabricated: 2,
    "third-party": 3
  };
  const classOrder: Record<ProjectFixtureControlsBomRow["itemClass"], number> = {
    controller: 0,
    device: 1,
    infrastructure: 2
  };

  return [...rows].sort((left, right) => {
    const sourceDelta =
      sourceOrder[left.supplySourceGroup] - sourceOrder[right.supplySourceGroup];

    if (sourceDelta !== 0) {
      return sourceDelta;
    }

    const typeDelta = classOrder[left.itemClass] - classOrder[right.itemClass];

    if (typeDelta !== 0) {
      return typeDelta;
    }

    return left.name.localeCompare(right.name, "en-GB");
  });
};

const uniqueStrings = (values: readonly string[]): string[] => [...new Set(values)];

const uniqueSourceRefs = (refs: readonly SourceRef[]): SourceRef[] => {
  const seen = new Set<string>();
  const result: SourceRef[] = [];

  for (const ref of refs) {
    const key = JSON.stringify(ref);

    if (!seen.has(key)) {
      seen.add(key);
      result.push(ref);
    }
  }

  return result;
};

type StoredBomFields = Pick<
  ExportPreviewBomRowDetail,
  "provisionScope" | "installIncluded" | "provisionScopeBasis" | "installIncludedBasis"
>;

const storedBomFieldProps = (input: {
  provisionScope?: StoredBomFields["provisionScope"] | undefined;
  installIncluded?: StoredBomFields["installIncluded"] | undefined;
  provisionScopeBasis?: StoredBomFields["provisionScopeBasis"] | undefined;
  installIncludedBasis?: StoredBomFields["installIncludedBasis"] | undefined;
}): StoredBomFields => ({
  ...(input.provisionScope ? { provisionScope: input.provisionScope } : {}),
  ...(input.installIncluded !== undefined
    ? { installIncluded: input.installIncluded }
    : {}),
  ...(input.provisionScopeBasis
    ? { provisionScopeBasis: input.provisionScopeBasis }
    : {}),
  ...(input.installIncludedBasis
    ? { installIncludedBasis: input.installIncludedBasis }
    : {})
});

const storedBomFieldsBySpecItemRef = (
  fixture: ProjectFixtureForDisplay
): ReadonlyMap<string, StoredBomFields> => {
  const fields = new Map<string, StoredBomFields>();

  for (const solution of fixture.apartmentSolutions) {
    for (const device of solution.devices) {
      fields.set(device.id, {
        ...(device.provisionScope ? { provisionScope: device.provisionScope } : {}),
        ...(device.provisionScopeBasis
          ? { provisionScopeBasis: device.provisionScopeBasis }
          : {})
      });
    }

    for (const item of solution.infrastructure) {
      fields.set(item.id, {
        ...(item.provisionScope ? { provisionScope: item.provisionScope } : {}),
        ...(item.provisionScopeBasis
          ? { provisionScopeBasis: item.provisionScopeBasis }
          : {}),
        installIncluded: item.installIncluded,
        ...(item.installIncludedBasis
          ? { installIncludedBasis: item.installIncludedBasis }
          : {})
      });
    }
  }

  return fields;
};

const storedBomFieldsForRow = (
  row: ProjectFixtureControlsBomRow,
  fieldsBySpecItemRef: ReadonlyMap<string, StoredBomFields>
): StoredBomFields => {
  const sourceFields = row.specItemRefs.map((ref) => fieldsBySpecItemRef.get(ref));

  return storedBomFieldProps({
    provisionScope:
      row.provisionScope ?? commonStoredField(sourceFields, "provisionScope"),
    installIncluded:
      row.installIncluded ?? commonStoredField(sourceFields, "installIncluded"),
    provisionScopeBasis:
      row.provisionScopeBasis ?? commonStoredField(sourceFields, "provisionScopeBasis"),
    installIncludedBasis:
      row.installIncludedBasis ??
      commonStoredField(sourceFields, "installIncludedBasis")
  });
};

const commonStoredFields = (rows: readonly StoredBomFields[]): StoredBomFields =>
  storedBomFieldProps({
    provisionScope: commonStoredField(rows, "provisionScope"),
    installIncluded: commonStoredField(rows, "installIncluded"),
    provisionScopeBasis: commonStoredField(rows, "provisionScopeBasis"),
    installIncludedBasis: commonStoredField(rows, "installIncludedBasis")
  });

function commonStoredField<Key extends keyof StoredBomFields>(
  rows: readonly (StoredBomFields | undefined)[],
  key: Key
): StoredBomFields[Key] | undefined {
  if (rows.length === 0 || rows.some((row) => row?.[key] === undefined)) {
    return undefined;
  }

  const first = rows[0]?.[key];

  return rows.every((row) => row?.[key] === first) ? first : undefined;
}

const storedSemanticKey = (row: StoredBomFields): string =>
  [
    row.provisionScope ?? "not_specified",
    row.installIncluded === undefined ? "not_specified" : String(row.installIncluded)
  ].join("|");

const aggregateIdFor = (row: ExportPreviewBomRowDisplay): string => {
  const code = row.productPartNo ?? row.itemKind;
  const slug =
    `${row.itemClass}-${code}-${row.supplySourceGroup}-${storedSemanticKey(row)}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "item";

  return `bom-rollup-${slug}`;
};

const aggregateKeyFor = (row: ExportPreviewBomRowDisplay): string =>
  [
    row.productPartNo ?? row.itemKind,
    row.itemClass,
    row.supplySourceGroup,
    storedSemanticKey(row)
  ].join("|");

const authoritativeExportSupplySource = (input: {
  productPartNo?: string;
  itemKind: string;
  supplySource: ProjectFixtureOutputSupplySource;
}): ProjectFixtureOutputSupplySource => {
  const descriptor = `${input.productPartNo ?? ""} ${input.itemKind}`
    .trim()
    .toLowerCase();

  return descriptor.includes("by-others") || descriptor.includes("by_others")
    ? "third-party"
    : input.supplySource;
};

const authoritativeExportSupplySourceLabel = (input: {
  productPartNo?: string;
  itemKind: string;
  supplySource: ProjectFixtureOutputSupplySource;
}): ExportPreviewBomRowDisplay["supplySourceLabel"] => {
  const descriptor = `${input.productPartNo ?? ""} ${input.itemKind}`
    .trim()
    .toLowerCase();

  return descriptor.includes("by-others") || descriptor.includes("by_others")
    ? "By others"
    : sourceLabel(authoritativeExportSupplySource(input));
};

const equivalentExportRowKey = (row: ExportPreviewBomRowDisplay): string =>
  [
    row.apartmentTypeId,
    row.productPartNo?.trim().toUpperCase() ?? row.itemKind,
    row.supplySourceGroup
  ].join("|");

const reconcileEquivalentBomRows = (
  rows: readonly ExportPreviewBomRowDisplay[]
): ExportPreviewBomRowDisplay[] => {
  const groups = new Map<string, ExportPreviewBomRowDisplay[]>();

  for (const row of rows) {
    const key = equivalentExportRowKey(row);
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }

  return [...groups.values()].flatMap((group) => {
    const itemClasses = new Set(group.map((row) => row.itemClass));
    const quantities = new Set(group.map((row) => row.projectQuantity));
    const canReconcile =
      group.length > 1 &&
      group.every((row) => Boolean(row.productPartNo)) &&
      itemClasses.size > 1 &&
      quantities.size === 1;

    if (!canReconcile) {
      return group;
    }

    const preferred =
      group.find((row) => row.itemClass === "controller") ??
      group.find((row) => row.itemClass === "device" && row.itemKind !== "other") ??
      group.find((row) => row.itemClass === "device") ??
      group[0];

    if (!preferred) {
      return group;
    }

    const sourceDetails = group.flatMap((row) => row.detailRows);
    const preferredDetail = preferred.detailRows[0] ?? sourceDetails[0];

    if (!preferredDetail) {
      return group;
    }

    const sourceRefs = uniqueSourceRefs(group.flatMap((row) => row.sourceRefs));
    const ruleTraceRefs = uniqueStrings(group.flatMap((row) => row.ruleTraceRefs));
    const riskRefs = uniqueStrings(group.flatMap((row) => row.riskRefs));
    const specItemRefs = uniqueStrings(group.flatMap((row) => row.specItemRefs));
    const installNotes = uniqueStrings(group.flatMap((row) => row.installNotes));
    const storedFields = storedBomFieldProps({
      provisionScope: sourceDetails.find((row) => row.provisionScope)?.provisionScope,
      installIncluded: sourceDetails.find((row) => row.installIncluded !== undefined)
        ?.installIncluded,
      provisionScopeBasis: sourceDetails.find((row) => row.provisionScopeBasis)
        ?.provisionScopeBasis,
      installIncludedBasis: sourceDetails.find((row) => row.installIncludedBasis)
        ?.installIncludedBasis
    });

    const detail: ExportPreviewBomRowDetail = {
      ...preferredDetail,
      ...storedFields,
      quantityPerApartmentType: Math.max(
        ...sourceDetails.map((row) => row.quantityPerApartmentType)
      ),
      apartmentTypeQuantity: Math.max(
        ...sourceDetails.map((row) => row.apartmentTypeQuantity)
      ),
      projectQuantity: Math.max(...sourceDetails.map((row) => row.projectQuantity)),
      installNotes,
      sourceRefs,
      sourceRefCount: sourceRefs.length,
      ruleTraceRefs,
      riskRefs,
      specItemRefs
    };

    return [
      {
        ...preferred,
        ...storedFields,
        quantityPerApartmentType: detail.quantityPerApartmentType,
        apartmentTypeQuantity: detail.apartmentTypeQuantity,
        projectQuantity: detail.projectQuantity,
        why: uniqueStrings(group.map((row) => row.why)).join(" "),
        how: uniqueStrings(group.map((row) => row.how)).join(" "),
        installNotes,
        sourceRefs,
        sourceRefCount: sourceRefs.length,
        ruleTraceRefs,
        riskRefs,
        specItemRefs,
        sourceRowCount: group.reduce((sum, row) => sum + row.sourceRowCount, 0),
        detailRows: [detail]
      }
    ];
  });
};

const provenanceWhereForRow = (input: {
  sourceRefCount: number;
  ruleTraceRefCount: number;
  sourceRowCount: number;
}): string => {
  const parts = [
    input.sourceRefCount > 0
      ? `${input.sourceRefCount} source${input.sourceRefCount === 1 ? "" : "s"}`
      : undefined,
    input.ruleTraceRefCount > 0
      ? `${input.ruleTraceRefCount} check${input.ruleTraceRefCount === 1 ? "" : "s"}`
      : undefined,
    input.sourceRowCount > 0
      ? `${input.sourceRowCount} evidence row${input.sourceRowCount === 1 ? "" : "s"}`
      : undefined
  ].filter((part): part is string => part !== undefined);

  return parts.length > 0
    ? `Where: ${parts.join(" and ")} retained for this item.`
    : "Where: no source trail is available in this view.";
};

const rollUpBomRows = (
  rows: readonly ExportPreviewBomRowDisplay[]
): ExportPreviewBomRowDisplay[] => {
  const groups = new Map<string, ExportPreviewBomRowDisplay[]>();

  for (const row of rows) {
    const key = aggregateKeyFor(row);
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }

  return sortBomRows(
    [...groups.values()].map((group) => {
      const first = group[0];

      if (!first) {
        throw new Error("Cannot roll up an empty BOM group.");
      }

      const detailRows = group.flatMap((row) => row.detailRows);
      const sourceRefs = uniqueSourceRefs(group.flatMap((row) => row.sourceRefs));
      const installNotes = uniqueStrings(group.flatMap((row) => row.installNotes));
      const ruleTraceRefs = uniqueStrings(group.flatMap((row) => row.ruleTraceRefs));
      const riskRefs = uniqueStrings(group.flatMap((row) => row.riskRefs));
      const specItemRefs = uniqueStrings(group.flatMap((row) => row.specItemRefs));
      const apartmentTypeIds = uniqueStrings(
        detailRows.map((detail) => detail.apartmentTypeId)
      );
      const apartmentTypeLabels = uniqueStrings(
        detailRows.map((detail) => detail.apartmentTypeLabel)
      );

      const sourceRefCount = sourceRefs.length;
      const ruleTraceRefCount = ruleTraceRefs.length;
      const sourceRowCount = group.reduce((sum, row) => sum + row.sourceRowCount, 0);
      const defaultApplied = group.some((row) => row.defaultApplied === true);
      const storedFields = commonStoredFields(detailRows);
      const firstWithoutStoredFields = { ...first };

      delete firstWithoutStoredFields.provisionScope;
      delete firstWithoutStoredFields.installIncluded;
      delete firstWithoutStoredFields.provisionScopeBasis;
      delete firstWithoutStoredFields.installIncludedBasis;

      return {
        ...firstWithoutStoredFields,
        id: aggregateIdFor(first),
        apartmentTypeId: "project",
        apartmentTypeLabel: "Whole job",
        apartmentTypeIds,
        apartmentTypeLabels,
        ...storedFields,
        quantityPerApartmentType: detailRows.reduce(
          (sum, row) => sum + row.quantityPerApartmentType,
          0
        ),
        apartmentTypeQuantity: detailRows.reduce(
          (sum, row) => sum + row.apartmentTypeQuantity,
          0
        ),
        projectQuantity: detailRows.reduce((sum, row) => sum + row.projectQuantity, 0),
        installNotes,
        sourceRefs,
        defaultApplied,
        provenanceWhy: defaultApplied
          ? DEFAULT_EDGE_EXPORT_WHY
          : (first.provenanceWhy ?? first.why),
        provenanceWhere: defaultApplied
          ? DEFAULT_EDGE_EXPORT_WHERE
          : provenanceWhereForRow({
              sourceRefCount,
              ruleTraceRefCount,
              sourceRowCount
            }),
        sourceRefCount,
        ruleTraceRefs,
        riskRefs,
        specItemRefs,
        sourceRowCount,
        detailRows
      };
    })
  );
};

const scopedBomRow = (
  row: ExportPreviewBomRowDisplay,
  scopeId: string
): ExportPreviewBomRowDisplay | null => {
  if (scopeId === "job") {
    return row;
  }

  const detailRows = row.detailRows.filter(
    (detail) =>
      detail.apartmentTypeId === scopeId || detail.apartmentTypeId === "project"
  );

  if (detailRows.length === 0) {
    return null;
  }

  const sourceRefs = uniqueSourceRefs(
    detailRows.flatMap((detail) => detail.sourceRefs)
  );
  const ruleTraceRefs = uniqueStrings(
    detailRows.flatMap((detail) => detail.ruleTraceRefs)
  );
  const riskRefs = uniqueStrings(detailRows.flatMap((detail) => detail.riskRefs));
  const specItemRefs = uniqueStrings(
    detailRows.flatMap((detail) => detail.specItemRefs)
  );
  const installNotes = uniqueStrings(
    detailRows.flatMap((detail) => detail.installNotes)
  );
  const storedFields = commonStoredFields(detailRows);
  const rowWithoutStoredFields = { ...row };

  delete rowWithoutStoredFields.provisionScope;
  delete rowWithoutStoredFields.installIncluded;
  delete rowWithoutStoredFields.provisionScopeBasis;
  delete rowWithoutStoredFields.installIncludedBasis;

  return {
    ...rowWithoutStoredFields,
    apartmentTypeId: scopeId,
    apartmentTypeLabel: detailRows[0]?.apartmentTypeLabel ?? row.apartmentTypeLabel,
    apartmentTypeIds: uniqueStrings(detailRows.map((detail) => detail.apartmentTypeId)),
    apartmentTypeLabels: uniqueStrings(
      detailRows.map((detail) => detail.apartmentTypeLabel)
    ),
    ...storedFields,
    quantityPerApartmentType: detailRows.reduce(
      (sum, detail) => sum + detail.quantityPerApartmentType,
      0
    ),
    apartmentTypeQuantity: detailRows.reduce(
      (sum, detail) => sum + detail.apartmentTypeQuantity,
      0
    ),
    projectQuantity: detailRows.reduce(
      (sum, detail) => sum + detail.projectQuantity,
      0
    ),
    installNotes,
    provenanceWhere: row.defaultApplied
      ? DEFAULT_EDGE_EXPORT_WHERE
      : provenanceWhereForRow({
          sourceRefCount: sourceRefs.length,
          ruleTraceRefCount: ruleTraceRefs.length,
          sourceRowCount: detailRows.length
        }),
    sourceRefs,
    sourceRefCount: sourceRefs.length,
    ruleTraceRefs,
    riskRefs,
    specItemRefs,
    sourceRowCount: detailRows.length,
    detailRows
  };
};

const bomRows = (fixture: ProjectFixtureForDisplay): ExportPreviewBomRowDisplay[] => {
  const takeoffById = takeoffRowsByApartmentType(fixture);
  const persistedFieldsBySpecItemRef = storedBomFieldsBySpecItemRef(fixture);

  const sourceRows = fixture.outputBundle.bomRows
    .filter((row) => !isCablingBomRow(row))
    .map((row) => {
      const storedFields = storedBomFieldsForRow(row, persistedFieldsBySpecItemRef);
      const productPartNo = displayPartNo(row);
      const typeLabel =
        row.apartmentTypeId === "project"
          ? "Whole job"
          : apartmentTypeLabel(fixture, row.apartmentTypeId, takeoffById);
      const installNotesForRow = row.installNotes.map((note) =>
        cleanInstallNote(note, fixture)
      );
      const sourceRefs = row.sourceRefs;
      const detailRows: ExportPreviewBomRowDetail[] = [
        {
          id: row.id,
          apartmentTypeId: row.apartmentTypeId,
          apartmentTypeLabel: typeLabel,
          ...storedFields,
          quantityPerApartmentType: row.quantityPerApartmentType,
          apartmentTypeQuantity: row.apartmentTypeQuantity,
          projectQuantity: row.projectQuantity,
          installNotes: installNotesForRow,
          sourceRefs,
          sourceRefCount: sourceRefs.length,
          ruleTraceRefs: row.ruleTraceRefs,
          riskRefs: row.riskRefs,
          specItemRefs: row.specItemRefs
        }
      ];

      return {
        id: row.id,
        apartmentTypeId: row.apartmentTypeId,
        apartmentTypeLabel: typeLabel,
        apartmentTypeIds: [row.apartmentTypeId],
        apartmentTypeLabels: [typeLabel],
        itemClass: row.itemClass,
        itemKind: row.itemKind,
        itemKindLabel: itemKindLabel(row),
        ...(productPartNo ? { productPartNo } : {}),
        name: itemName(row),
        role: humaniseExportDisplayText(row.description),
        description: itemDescription(row),
        supplySource: authoritativeExportSupplySource(row),
        supplySourceGroup: sourceGroup(authoritativeExportSupplySource(row)),
        supplySourceLabel: authoritativeExportSupplySourceLabel(row),
        ...storedFields,
        quantityPerApartmentType: row.quantityPerApartmentType,
        apartmentTypeQuantity: row.apartmentTypeQuantity,
        projectQuantity: row.projectQuantity,
        why: cleanBomProse(row.why, row),
        how: cleanBomProse(row.how, row),
        defaultApplied: false,
        provenanceWhy: cleanBomProse(row.why, row),
        provenanceWhere: provenanceWhereForRow({
          sourceRefCount: sourceRefs.length,
          ruleTraceRefCount: row.ruleTraceRefs.length,
          sourceRowCount: 1
        }),
        installNotes: installNotesForRow,
        sourceRefs,
        sourceRefCount: sourceRefs.length,
        ruleTraceRefs: row.ruleTraceRefs,
        riskRefs: row.riskRefs,
        specItemRefs: row.specItemRefs,
        sourceRowCount: 1,
        detailRows
      };
    });
  const rows = shouldAddDefaultEdgeRows(fixture)
    ? [...sourceRows, ...defaultEdgeBomRows(fixture)]
    : sourceRows;

  return rollUpBomRows(reconcileEquivalentBomRows(rows));
};

const removedDefaultEdgePartNos = (
  fixture: ProjectFixtureForDisplay
): ReadonlySet<string> =>
  new Set(
    fixture.apartmentSolutions.flatMap((solution) =>
      solution.justifications.flatMap((justification) => {
        const removedPartNo = defaultEdgeRemovalPartNoFromTargetRef(
          justification.targetRef
        );

        return removedPartNo ? [removedPartNo] : [];
      })
    )
  );

const shouldAddDefaultEdgeRows = (fixture: ProjectFixtureForDisplay): boolean =>
  fixture.apartmentSolutions.length > 0 &&
  fixture.apartmentSolutions.every((solution) => shouldInjectDefaultEdgeRows(solution));

const defaultEdgeBomRows = (
  fixture: ProjectFixtureForDisplay
): ExportPreviewBomRowDisplay[] =>
  DEFAULT_EDGE_PRODUCTS.filter(
    (product) => !removedDefaultEdgePartNos(fixture).has(product.productPartNo)
  ).map((product) => {
    const rowId = `bom-default-edge-${fixture.project.projectId}-${product.itemKind}`;
    const productName = defaultEdgeProductName(product);
    const productDescription = defaultEdgeProductDescription(product);
    const installNotes = [defaultEdgeInstallNote(product)];
    const detailRows: ExportPreviewBomRowDetail[] = [
      {
        id: rowId,
        apartmentTypeId: "project",
        apartmentTypeLabel: "Whole job",
        quantityPerApartmentType: 1,
        apartmentTypeQuantity: 1,
        projectQuantity: 1,
        installNotes,
        sourceRefs: [],
        sourceRefCount: 0,
        ruleTraceRefs: [],
        riskRefs: [],
        specItemRefs: []
      }
    ];

    return {
      id: rowId,
      apartmentTypeId: "project",
      apartmentTypeLabel: "Whole job",
      apartmentTypeIds: ["project"],
      apartmentTypeLabels: ["Whole job"],
      itemClass: "infrastructure",
      itemKind: product.itemKind,
      itemKindLabel: humaniseExportDisplayText(product.itemKind),
      productPartNo: product.productPartNo,
      name: clientProductLabel(productName, product.productPartNo),
      role: clientProductLabel(productDescription, product.productPartNo),
      description: clientProductLabel(productDescription, product.productPartNo),
      supplySource: defaultEdgeSupplySource(product.supplySource),
      supplySourceGroup: sourceGroup(defaultEdgeSupplySource(product.supplySource)),
      supplySourceLabel: sourceLabel(defaultEdgeSupplySource(product.supplySource)),
      quantityPerApartmentType: 1,
      apartmentTypeQuantity: 1,
      projectQuantity: 1,
      why: DEFAULT_EDGE_EXPORT_WHY,
      how: DEFAULT_EDGE_EXPORT_WHERE,
      defaultApplied: true,
      provenanceWhy: DEFAULT_EDGE_EXPORT_WHY,
      provenanceWhere: DEFAULT_EDGE_EXPORT_WHERE,
      installNotes,
      sourceRefs: [],
      sourceRefCount: 0,
      ruleTraceRefs: [],
      riskRefs: [],
      specItemRefs: [],
      sourceRowCount: 1,
      detailRows
    };
  });

const scopeOptions = (fixture: ProjectFixtureForDisplay): ExportScopeOption[] => {
  const takeoffById = takeoffRowsByApartmentType(fixture);

  return [
    {
      id: "job",
      label: "Whole job",
      kind: "job",
      quantity: fixture.takeoffRows.reduce((sum, row) => sum + row.quantity, 0)
    },
    ...fixture.takeoffRows.map((row) => ({
      id: row.apartmentTypeId,
      label: apartmentTypeLabel(fixture, row.apartmentTypeId, takeoffById),
      kind: "apartmentType" as const,
      quantity: row.quantity
    }))
  ];
};

const requirements = (
  fixture: ProjectFixtureForDisplay
): ExportPreviewRequirementRow[] =>
  fixture.project.requirements.map((requirement) => ({
    id: requirement.id,
    text: humaniseExportText(requirement.interpretedText),
    kindLabel: labelise(requirement.type),
    statusLabel: labelise(requirement.status),
    confidence: requirement.confidence,
    confidenceLabel: `${Math.round(requirement.confidence * 100)}% confidence`,
    sourceRefs: requirement.sourceRefs ?? [],
    sourceRefCount: requirement.sourceRefs?.length ?? 0
  }));

const installNotes = (
  rows: readonly ExportPreviewBomRowDisplay[]
): ExportPreviewInstallNote[] =>
  rows.flatMap((row) =>
    row.installNotes.map((note, index) => ({
      id: `${row.id}-install-${index + 1}`,
      itemLabel: row.name,
      apartmentTypeId: row.apartmentTypeId,
      apartmentTypeLabel: row.apartmentTypeLabel,
      note,
      why: row.why,
      riskRefs: row.riskRefs
    }))
  );

const countLabel = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

const optionalSourceRefCount = (value: unknown): number => {
  if (
    value &&
    typeof value === "object" &&
    "sourceRefs" in value &&
    Array.isArray((value as { sourceRefs?: unknown[] }).sourceRefs)
  ) {
    return (value as { sourceRefs: unknown[] }).sourceRefs.length;
  }

  return 0;
};

const takeoffRows = (fixture: ProjectFixtureForDisplay): ExportPreviewTakeoffRow[] => {
  const takeoffById = takeoffRowsByApartmentType(fixture);
  const manualTakeoffEdits = new Set(
    (fixture.fixture.compareMatrixOverrides ?? [])
      .filter((override) => override.groupId === "takeoffs")
      .map((override) => override.apartmentTypeId)
  );

  return fixture.takeoffRows.map((row) => {
    const label = apartmentTypeLabel(fixture, row.apartmentTypeId, takeoffById);
    const bedsLabel =
      typeof row.beds === "number"
        ? row.beds === 0
          ? "Studio"
          : countLabel(row.beds, "bedroom")
        : "Bedroom count not stated";
    const fcuCountLabel =
      typeof row.fcuCount === "number"
        ? countLabel(row.fcuCount, "fan-coil unit")
        : "Fan-coil count not stated";

    return {
      id: `takeoff-${row.apartmentTypeId}`,
      apartmentTypeId: row.apartmentTypeId,
      apartmentTypeLabel: label,
      quantity: row.quantity,
      bedsLabel,
      fcuCountLabel,
      aiWhat: `AI extracted ${countLabel(row.quantity, "home")} for ${label}.`,
      aiWhy: manualTakeoffEdits.has(row.apartmentTypeId)
        ? "The take-off sets the multiplier used by the products, apartment schedule and quote package. It includes a user-edited value from the Take-offs stage."
        : "The take-off sets the multiplier used by the products, apartment schedule and quote package.",
      traceability: "Retained from the structured apartment take-off.",
      sourceRefCount: optionalSourceRefCount(row)
    };
  });
};

const sumDetailQuantity = (
  rows: readonly ExportPreviewBomRowDisplay[],
  apartmentTypeId: string,
  itemClass: ProjectFixtureControlsBomRow["itemClass"]
): number =>
  rows.reduce(
    (sum, row) =>
      sum +
      row.detailRows
        .filter(
          (detail) =>
            detail.apartmentTypeId === apartmentTypeId && row.itemClass === itemClass
        )
        .reduce((inner, detail) => inner + detail.quantityPerApartmentType, 0),
    0
  );

const apartmentSchedules = (
  fixture: ProjectFixtureForDisplay,
  rows: readonly ExportPreviewBomRowDisplay[]
): ExportPreviewApartmentScheduleRow[] => {
  const takeoffById = takeoffRowsByApartmentType(fixture);

  return fixture.takeoffRows.flatMap((takeoff) => {
    const label = apartmentTypeLabel(fixture, takeoff.apartmentTypeId, takeoffById);
    const detailRows = rows.flatMap((row) =>
      row.detailRows
        .filter((detail) => detail.apartmentTypeId === takeoff.apartmentTypeId)
        .map((detail) => ({ row, detail }))
    );
    const scheduledDetailRows = detailRows.filter(
      ({ detail }) => detail.quantityPerApartmentType > 0
    );

    if (scheduledDetailRows.length === 0) {
      return [];
    }

    const featuredProducts = uniqueStrings(
      scheduledDetailRows.map(({ row }) => row.name)
    ).slice(0, 6);
    const sourceRefCount = uniqueSourceRefs(
      scheduledDetailRows.flatMap(({ detail }) => detail.sourceRefs)
    ).length;
    const ruleTraceRefCount = uniqueStrings(
      scheduledDetailRows.flatMap(({ detail }) => detail.ruleTraceRefs)
    ).length;
    const controllersPerHome = sumDetailQuantity(
      rows,
      takeoff.apartmentTypeId,
      "controller"
    );
    const devicesPerHome = sumDetailQuantity(rows, takeoff.apartmentTypeId, "device");
    const infrastructurePerHome = sumDetailQuantity(
      rows,
      takeoff.apartmentTypeId,
      "infrastructure"
    );

    return [
      {
        id: `apartment-schedule-${takeoff.apartmentTypeId}`,
        apartmentTypeId: takeoff.apartmentTypeId,
        apartmentTypeLabel: label,
        homes: takeoff.quantity,
        controllersPerHome,
        devicesPerHome,
        infrastructurePerHome,
        featuredProducts,
        aiWhat: `AI scheduled ${controllersPerHome} controller${controllersPerHome === 1 ? "" : "s"}, ${devicesPerHome} room device${devicesPerHome === 1 ? "" : "s"} and ${infrastructurePerHome} infrastructure item${infrastructurePerHome === 1 ? "" : "s"} per ${label} home.`,
        aiWhy:
          "The schedule rolls the product selection back to each apartment type so the client can review what is included per home.",
        sourceRefCount,
        ruleTraceRefCount
      }
    ];
  });
};

const cleanRfiText = (value: string): string =>
  humaniseExportText(
    value
      .replace(
        /\bcandidate confidence ([0-9.]+)\s*<\s*([0-9.]+)\b/gi,
        "requirement candidate confidence $1 is below the $2 approval threshold"
      )
      .replace(/\bneedsUserDecision\b/g, "needs your decision")
      .replace(/\bdefaultDisposition\b/g, "default answer")
  );

const normalizeRfiKeyText = (value: string): string =>
  cleanRfiText(value).toLowerCase().replace(/\s+/g, " ").trim();

const dedupeRfiRows = (rows: readonly ExportPreviewRfiRow[]): ExportPreviewRfiRow[] => {
  const byQuestion = new Map<string, ExportPreviewRfiRow>();

  for (const row of rows) {
    const key = [
      normalizeRfiKeyText(row.title),
      normalizeRfiKeyText(row.question),
      normalizeRfiKeyText(row.priorityLabel)
    ].join("\u001f");
    const existing = byQuestion.get(key);

    if (!existing) {
      byQuestion.set(key, row);
      continue;
    }

    byQuestion.set(key, {
      ...existing,
      affectedLabel: uniqueStrings([
        ...existing.affectedLabel.split(/\s*,\s*/),
        ...row.affectedLabel.split(/\s*,\s*/)
      ]).join(", ")
    });
  }

  return [...byQuestion.values()];
};

const rfiRows = (fixture: ProjectFixtureForDisplay): ExportPreviewRfiRow[] => {
  const riskRows = fixture.risks
    .filter((risk) => risk.status === "open" && risk.type === "needsUserDecision")
    .map((risk, index) => {
      const scope = (risk as { scope?: { label?: string; refId?: string } }).scope;
      const affectedLabel = humaniseExportText(
        scope?.label ?? (scope?.refId ? labelise(scope.refId) : "Whole job")
      );

      return {
        id: `rfi-risk-${index + 1}`,
        title: cleanRfiText(risk.title ?? "Decision needed"),
        question: cleanRfiText(
          risk.title ??
            "Confirm the product or scope decision before this item is issued."
        ),
        affectedLabel,
        priorityLabel: risk.blocksExport ? "Blocks issue" : "Confirm before issue",
        aiWhy:
          "AI carried this as a client decision instead of silently resolving the uncertainty.",
        traceability: "Retained from the needs-your-decision register."
      };
    });
  const decisionRows = fixture.openDecisions.map((decision, index) => ({
    id: `rfi-open-decision-${index + 1}`,
    title: "Question for client review",
    question: cleanRfiText(
      decision.message ?? "Confirm this open project decision before issue."
    ),
    affectedLabel: "Whole job",
    priorityLabel: decision.stage === "output" ? "Final output" : "Design review",
    aiWhy:
      "AI surfaced this question because the source data did not give a high-confidence answer.",
    traceability: "Retained from the open-decision register."
  }));

  return dedupeRfiRows([...riskRows, ...decisionRows]);
};

const protocolLabelFor = (text: string): string => {
  if (/bacnet\/ip/i.test(text)) {
    return "BACnet/IP";
  }

  if (/bacnet/i.test(text)) {
    return "BACnet";
  }

  if (/modbus|rs[-\s]?485/i.test(text)) {
    return "Modbus / RS-485";
  }

  if (/ethernet|network/i.test(text)) {
    return "Ethernet";
  }

  if (/software|pair/i.test(text)) {
    return "Software pairing";
  }

  return "Controls cabling method";
};

const cablingRowsFromMappings = (
  rows: readonly ExportPreviewMappingRow[]
): ExportPreviewCablingRow[] => {
  const byMethod = new Map<string, ExportPreviewMappingRow[]>();

  rows.forEach((row) => {
    const method = row.cableImplication?.trim();

    if (!method) {
      return;
    }

    const key = [row.apartmentTypeId, humaniseExportText(method).toLowerCase()].join(
      "|"
    );
    byMethod.set(key, [...(byMethod.get(key) ?? []), row]);
  });

  return [...byMethod.values()].map((group, index) => {
    const first = group[0];

    if (!first?.cableImplication) {
      throw new Error("Cannot build a cabling row without a cable implication.");
    }

    const method = humaniseExportText(first.cableImplication);
    const appliesTo = uniqueStrings(
      group.map((row) => `${row.deviceLabel} in ${row.roomLabel}`)
    );

    return {
      id: `cabling-${index + 1}`,
      apartmentTypeId: first.apartmentTypeId,
      apartmentTypeLabel: first.apartmentTypeLabel,
      method,
      appliesTo:
        appliesTo.length <= 3
          ? appliesTo.join("; ")
          : `${appliesTo.slice(0, 3).join("; ")} +${appliesTo.length - 3} more`,
      quantity: group.length,
      protocolLabel: protocolLabelFor(
        `${method} ${group.map((row) => row.deviceLabel).join(" ")}`
      ),
      aiWhy:
        "AI kept this as install-readiness guidance; it is not converted into cable-length or labour quantities.",
      sourceRefCount: uniqueSourceRefs(group.flatMap((row) => row.sourceRefs)).length,
      ruleTraceRefCount: uniqueStrings(group.flatMap((row) => row.ruleTraceRefs)).length
    };
  });
};

const detailRows = (
  fixture: ProjectFixtureForDisplay,
  notes: readonly ExportPreviewInstallNote[]
): ExportPreviewDetailRow[] => {
  const productPartNos = productPartNosForFixture(fixture);
  const installDetails = notes.map(
    (note, index): ExportPreviewDetailRow => ({
      id: `detail-install-${index + 1}`,
      apartmentTypeId: note.apartmentTypeId,
      title: note.itemLabel,
      detail: note.note,
      affectedLabel: note.apartmentTypeLabel,
      categoryLabel: "Install detail",
      aiWhy: note.why,
      sourceRefCount: 0,
      ruleTraceRefCount: 0
    })
  );
  const commissioningDetails = fixture.outputBundle.outputGraphs.flatMap((graph) => {
    const deviceById = new Map(
      graph.deviceInstances.flatMap((device) => [
        [device.id, device],
        [device.specItemRef, device]
      ])
    );

    return graph.commissioningTasks.map((task): ExportPreviewDetailRow => {
      const scopedDevices = task.deviceInstanceIds
        .map((id) => deviceById.get(id))
        .filter((device): device is ProjectFixtureOutputDeviceInstance =>
          Boolean(device?.apartmentTypeId)
        );
      const apartmentTypeIds = uniqueStrings(
        scopedDevices.map((device) => device.apartmentTypeId ?? "")
      ).filter(Boolean);
      const apartmentTypeId =
        apartmentTypeIds.length === 1 ? apartmentTypeIds[0] : undefined;

      return {
        id: `detail-${task.id}`,
        ...(apartmentTypeId ? { apartmentTypeId } : {}),
        title: clientDeliverableText(task.title, { productPartNos }),
        detail: clientDeliverableText(task.description, { productPartNos }),
        affectedLabel: apartmentTypeId
          ? apartmentTypeLabel(fixture, apartmentTypeId)
          : "Whole job",
        categoryLabel: labelise(task.status),
        aiWhy:
          "AI retained this commissioning detail so installation and handover checks stay visible.",
        sourceRefCount: task.sourceRefs.length,
        ruleTraceRefCount: task.ruleTraceRefs.length
      };
    });
  });

  return [...installDetails, ...commissioningDetails];
};

const specificationSections = (input: {
  requirements: readonly ExportPreviewRequirementRow[];
  rows: readonly ExportPreviewBomRowDisplay[];
  cabling: readonly ExportPreviewCablingRow[];
  edges: readonly ExportPreviewEdgeExtension[];
}): ExportPreviewSpecificationSection[] => {
  const approvedRequirements = input.requirements.slice(0, 5);
  const totalQuantity = input.rows.reduce((sum, row) => sum + row.projectQuantity, 0);
  const controllerQuantity = input.rows
    .filter((row) => row.itemClass === "controller")
    .reduce((sum, row) => sum + row.projectQuantity, 0);
  const deviceQuantity = input.rows
    .filter((row) => row.itemClass === "device")
    .reduce((sum, row) => sum + row.projectQuantity, 0);
  const edgeText =
    input.edges.length > 0
      ? `${input.edges.length} Edge extension${input.edges.length === 1 ? "" : "s"} included for leak detection, metering or handover connectivity.`
      : "Edge extension data is not available from the current data.";
  const sections: ExportPreviewSpecificationSection[] = [];

  if (approvedRequirements.length > 0) {
    sections.push({
      id: "specification-controls-intent",
      heading: "Controls specification",
      body: approvedRequirements.map((requirement) => requirement.text).join(" "),
      aiWhy:
        "AI turned the accepted requirements into the controls narrative used by the client package.",
      sourceRefCount: approvedRequirements.reduce(
        (sum, requirement) => sum + requirement.sourceRefCount,
        0
      )
    });
  }

  if (input.rows.length > 0) {
    sections.push({
      id: "specification-products",
      heading: "Products and controls strategy",
      body: `${countLabel(totalQuantity, "control item")} are included across the selected scope, including ${countLabel(controllerQuantity, "controller")} and ${countLabel(deviceQuantity, "room device")}.`,
      aiWhy:
        "AI rolled the selected controller, device and infrastructure items into a client-readable product strategy.",
      sourceRefCount: input.rows.reduce((sum, row) => sum + row.sourceRefCount, 0)
    });
  }

  if (input.cabling.length > 0 || input.edges.length > 0) {
    sections.push({
      id: "specification-cabling-handover",
      heading: "Cabling and handover approach",
      body: `${countLabel(input.cabling.length, "cabling method")} are retained as install-readiness guidance. ${edgeText}`,
      aiWhy:
        "AI kept methods, protocols and commissioning context visible without pretending cable lengths are priced here.",
      sourceRefCount: input.cabling.reduce((sum, row) => sum + row.sourceRefCount, 0)
    });
  }

  return sections;
};

const deferredScheduleExtensionPoints = (
  hasAcceptedScheduleEntries: boolean,
  hasProjectNotes: boolean
): ExportPreviewDeferredScheduleExtensionPoint[] => [
  ...(!hasAcceptedScheduleEntries
    ? [
        {
          id: "clarifications-exclusions-assumptions" as const,
          label: "Clarifications, exclusions and assumptions schedules",
          followUp: "D3 will generate dedicated client schedules for these records.",
          reason:
            "This chain does not yet have approved client-ready clarification, exclusion or assumption schedule records."
        }
      ]
    : []),
  ...(!hasProjectNotes
    ? [
        {
          id: "notes-client-schedule" as const,
          label: "Project notes register",
          followUp:
            "D5 exports reviewed project notes when saved note records are available.",
          reason: "No saved project notes are attached to this export view."
        }
      ]
    : [])
];

const refLabel = (
  graph: ProjectFixtureOutputGraph,
  deviceById: ReadonlyMap<string, ProjectFixtureOutputDeviceInstance>,
  ref: string | undefined,
  productPartNos: readonly string[] = []
): string | undefined => {
  if (!ref) {
    return undefined;
  }

  const device = deviceById.get(ref);

  if (device) {
    return clientDeliverableText(device.label, {
      productPartNos: [device.productPartNo, ...productPartNos]
    });
  }

  const product = graph.productQuantityTotals[ref];

  if (product !== undefined) {
    return clientDeliverableText(ref, { productPartNos });
  }

  return clientDeliverableText(ref, { productPartNos });
};

const mappingRows = (fixture: ProjectFixtureForDisplay): ExportPreviewMappingRow[] => {
  const takeoffById = takeoffRowsByApartmentType(fixture);
  const productPartNos = productPartNosForFixture(fixture);

  return fixture.outputBundle.outputGraphs.flatMap((graph) => {
    const deviceById = new Map(
      graph.deviceInstances.map((device) => [device.specItemRef, device])
    );

    return graph.mappingScheduleRows.map(
      (row: ProjectFixtureMappingScheduleRow): ExportPreviewMappingRow => {
        const controllerLabel = refLabel(
          graph,
          deviceById,
          row.logicalControllerRef,
          productPartNos
        );
        const terminationLabel = refLabel(
          graph,
          deviceById,
          row.physicalTerminationRef,
          productPartNos
        );

        return {
          id: row.id,
          apartmentTypeId: row.apartmentTypeId ?? "project",
          apartmentTypeLabel: row.apartmentTypeId
            ? apartmentTypeLabel(fixture, row.apartmentTypeId, takeoffById)
            : "Whole job",
          deviceLabel: clientDeliverableText(
            refLabel(graph, deviceById, row.deviceInstanceId, productPartNos) ??
              row.deviceInstanceId,
            { productPartNos }
          ),
          ...(controllerLabel ? { controllerLabel } : {}),
          ...(terminationLabel ? { terminationLabel } : {}),
          roomLabel: clientDeliverableText(row.room ?? "Room unspecified", {
            productPartNos
          }),
          ...(row.cableImplication
            ? {
                cableImplication: clientDeliverableText(row.cableImplication, {
                  productPartNos
                })
              }
            : {}),
          commissioningNote: clientDeliverableText(row.commissioningNote, {
            productPartNos
          }),
          riskRefs: row.riskRefs,
          sourceRefCount: row.sourceRefs.length,
          sourceRefs: row.sourceRefs,
          ruleTraceRefs: row.ruleTraceRefs
        };
      }
    );
  });
};

const edgeExtensions = (
  fixture: ProjectFixtureForDisplay,
  rows: readonly ExportPreviewBomRowDisplay[]
): ExportPreviewEdgeExtension[] => {
  const takeoffById = takeoffRowsByApartmentType(fixture);
  const grouped = new Map<
    string,
    {
      apartmentTypeLabels: Set<string>;
      provisionUnit: string;
      inputPolicy: string;
      connectivity: string;
      installNotes: Set<string>;
      needsUserDecision: boolean;
      bomRowIds: Set<string>;
    }
  >();

  for (const solution of fixture.apartmentSolutions) {
    for (const feature of solution.edgeFeatures ?? []) {
      const key = [
        feature.featureKind,
        feature.provisionUnit,
        feature.inputPolicy
      ].join("|");
      const existing = grouped.get(key) ?? {
        apartmentTypeLabels: new Set<string>(),
        provisionUnit: feature.provisionUnit,
        inputPolicy: feature.inputPolicy,
        connectivity: feature.connectivity ?? "Connectivity unspecified",
        installNotes: new Set<string>(),
        needsUserDecision: false,
        bomRowIds: new Set<string>()
      };

      existing.apartmentTypeLabels.add(
        apartmentTypeLabel(fixture, solution.apartmentTypeId, takeoffById)
      );
      feature.installNotes.forEach((note) =>
        existing.installNotes.add(cleanInstallNote(note, fixture))
      );
      existing.needsUserDecision =
        existing.needsUserDecision || feature.needsUserDecision;
      rows
        .filter(
          (row) =>
            row.detailRows.some(
              (detail) => detail.apartmentTypeId === solution.apartmentTypeId
            ) &&
            /edge|leak|meter/i.test(`${row.itemKind} ${row.description} ${row.name}`)
        )
        .forEach((row) => existing.bomRowIds.add(row.id));
      grouped.set(key, existing);
    }
  }

  if (grouped.size === 0) {
    const defaultBomRowIds = rows
      .filter((row) => row.defaultApplied === true)
      .map((row) => row.id);

    if (defaultBomRowIds.length === 0) {
      return [];
    }

    return [
      {
        id: "edge-default-capabilities",
        title: "Default Edge capabilities",
        apartmentTypeLabels: ["Whole job"],
        provisionUnit: "Project-level Edge allowance",
        inputPolicy: DEFAULT_NO_TRIGGER_LABEL,
        connectivity: "BACnet/IP dashboard and alert path",
        installNotes: ["Include one self-contained Edge gateway PC."],
        needsUserDecision: false,
        bomRowIds: defaultBomRowIds,
        defaultApplied: true,
        capabilities: [...DEFAULT_EDGE_CAPABILITIES],
        provenanceWhy: DEFAULT_EDGE_EXPORT_WHY,
        provenanceWhere: DEFAULT_EDGE_EXPORT_WHERE
      }
    ];
  }

  return [...grouped.entries()]
    .map(([key, value]) => ({
      id: `edge-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
      title: humaniseExportText(labelise(key.split("|")[0] ?? "edge extension")),
      apartmentTypeLabels: [...value.apartmentTypeLabels].sort((left, right) =>
        left.localeCompare(right, "en-GB")
      ),
      provisionUnit: humaniseExportText(labelise(value.provisionUnit)),
      inputPolicy: humaniseExportText(labelise(value.inputPolicy)),
      connectivity: humaniseExportText(value.connectivity),
      installNotes: [...value.installNotes],
      needsUserDecision: value.needsUserDecision,
      bomRowIds: [...value.bomRowIds],
      defaultApplied: false,
      capabilities: [
        "Edge feature allowance",
        humaniseExportText(value.connectivity),
        value.needsUserDecision ? "Review decision required" : "Ready for handover"
      ],
      provenanceWhy: "AI-produced Edge extension derived from project requirements.",
      provenanceWhere: `Where: ${value.apartmentTypeLabels.size} apartment type${value.apartmentTypeLabels.size === 1 ? "" : "s"} and ${value.bomRowIds.size} item line${value.bomRowIds.size === 1 ? "" : "s"} attached.`
    }))
    .sort((left, right) => left.title.localeCompare(right.title, "en-GB"));
};

const supplySourceBreakdown = (rows: readonly ExportPreviewBomRowDisplay[]) => ({
  "titan-supplied": {
    label: "Titan-supplied",
    rowCount: rows.filter((row) => row.supplySourceGroup === "titan-supplied").length,
    quantity: rows
      .filter((row) => row.supplySourceGroup === "titan-supplied")
      .reduce((sum, row) => sum + row.projectQuantity, 0)
  },
  "in-house": {
    label: "Automatry Edge",
    rowCount: rows.filter((row) => row.supplySourceGroup === "in-house").length,
    quantity: rows
      .filter((row) => row.supplySourceGroup === "in-house")
      .reduce((sum, row) => sum + row.projectQuantity, 0)
  },
  fabricated: {
    label: "Fabricated",
    rowCount: rows.filter((row) => row.supplySourceGroup === "fabricated").length,
    quantity: rows
      .filter((row) => row.supplySourceGroup === "fabricated")
      .reduce((sum, row) => sum + row.projectQuantity, 0)
  },
  "third-party": {
    label: "Third-party",
    rowCount: rows.filter((row) => row.supplySourceGroup === "third-party").length,
    quantity: rows
      .filter((row) => row.supplySourceGroup === "third-party")
      .reduce((sum, row) => sum + row.projectQuantity, 0)
  }
});

const hasApartmentScheduleGap = (input: {
  takeoffs: readonly ExportPreviewTakeoffRow[];
  schedules: readonly ExportPreviewApartmentScheduleRow[];
}): boolean =>
  input.schedules.length === 0 || input.schedules.length < input.takeoffs.length;

const dataGapsFor = (input: {
  fixture: ProjectFixtureForDisplay;
  rows: readonly ExportPreviewBomRowDisplay[];
  installNotes: readonly ExportPreviewInstallNote[];
  projectNotes: readonly ExportPreviewNoteRow[];
  takeoffs: readonly ExportPreviewTakeoffRow[];
  schedules: readonly ExportPreviewApartmentScheduleRow[];
  scheduleEntries: readonly ExportPreviewScheduleEntry[];
  rfis: readonly ExportPreviewRfiRow[];
  mappings: readonly ExportPreviewMappingRow[];
  cabling: readonly ExportPreviewCablingRow[];
  details: readonly ExportPreviewDetailRow[];
  specification: readonly ExportPreviewSpecificationSection[];
  edges: readonly ExportPreviewEdgeExtension[];
}): string[] => [
  ...(input.fixture.fixture.partialScope
    ? [input.fixture.fixture.partialScope.message]
    : []),
  ...(input.fixture.fixture.draftScope
    ? [input.fixture.fixture.draftScope.message]
    : []),
  ...(input.takeoffs.length === 0
    ? ["Take-off rows are not available from the current data."]
    : []),
  ...(input.rows.length === 0
    ? ["Product rows are not available from the current data."]
    : []),
  ...(hasApartmentScheduleGap(input)
    ? ["Apartment schedule rows are not available from the current data."]
    : []),
  ...(input.rfis.length === 0
    ? ["RFI rows are not available from the current data."]
    : []),
  ...(input.cabling.length === 0
    ? ["Cabling rows are not available from the current data."]
    : []),
  ...(input.details.length === 0
    ? [
        "Installation and commissioning detail rows are not available from the current data."
      ]
    : []),
  ...(input.specification.length === 0
    ? ["Specification narrative is not available from the current data."]
    : []),
  ...(input.installNotes.length === 0
    ? ["Install notes are not available from the current data."]
    : []),
  ...(input.scheduleEntries.length === 0
    ? [
        "Accepted clarification, exclusion and assumption schedule entries are not available from the current data."
      ]
    : []),
  ...(input.projectNotes.length === 0
    ? ["Project note rows are not available from the current data."]
    : []),
  ...(input.mappings.length === 0
    ? [
        "Mapping and commissioning schedule rows are not available from the current data."
      ]
    : []),
  ...(input.edges.length === 0
    ? ["Edge extension rows are not available from the current data."]
    : []),
  ...(input.edges.some((edge) => edge.defaultApplied === true)
    ? [
        `${DEFAULT_NO_TRIGGER_LABEL}. Edge is included in this export without a source trail because no project-specific Edge requirement was found.`
      ]
    : []),
  ...(input.fixture.outputBundle.exportBundles.length === 0
    ? ["Export reference records are not available from the current data."]
    : []),
  "PDF export, labour pricing, and cable-length quantities are not included in the current bundle."
];

export const filterExportBomRows = (
  rows: readonly ExportPreviewBomRowDisplay[],
  scopeId: string,
  sourceFilter: ExportBomFilter = "all"
): ExportPreviewBomRowDisplay[] =>
  rows.flatMap((row) => {
    const sourceMatches =
      sourceFilter === "all" || row.supplySourceGroup === sourceFilter;

    if (!sourceMatches) {
      return [];
    }

    const scoped = scopedBomRow(row, scopeId);

    return scoped ? [scoped] : [];
  });

export const filterExportTakeoffRows = (
  rows: readonly ExportPreviewTakeoffRow[],
  scopeId: string
): ExportPreviewTakeoffRow[] =>
  scopeId === "job" ? [...rows] : rows.filter((row) => row.apartmentTypeId === scopeId);

export const filterExportApartmentSchedules = (
  rows: readonly ExportPreviewApartmentScheduleRow[],
  scopeId: string
): ExportPreviewApartmentScheduleRow[] =>
  scopeId === "job" ? [...rows] : rows.filter((row) => row.apartmentTypeId === scopeId);

export const filterExportCablingRows = (
  rows: readonly ExportPreviewCablingRow[],
  scopeId: string
): ExportPreviewCablingRow[] =>
  scopeId === "job" ? [...rows] : rows.filter((row) => row.apartmentTypeId === scopeId);

export const filterExportDetailRows = (
  rows: readonly ExportPreviewDetailRow[],
  scopeId: string
): ExportPreviewDetailRow[] =>
  scopeId === "job"
    ? [...rows]
    : rows.filter((row) => !row.apartmentTypeId || row.apartmentTypeId === scopeId);

export const filterExportScheduleEntries = (
  rows: readonly ExportPreviewScheduleEntry[],
  scopeId: string
): ExportPreviewScheduleEntry[] =>
  scopeId === "job"
    ? [...rows]
    : rows.filter(
        (row) =>
          row.affectedApartmentTypeIds.length === 0 ||
          row.affectedApartmentTypeIds.includes(scopeId)
      );

export const filterExportNoteRows = (
  rows: readonly ExportPreviewNoteRow[],
  _scopeId: string
): ExportPreviewNoteRow[] => [...rows];

export interface ExportClientPackageInput {
  projectLabel: string;
  projectId?: string;
  selectedScopeLabel: string;
  scopeId?: string;
  exportBundleId?: string;
  outputBundleHash?: string;
  generatedAt?: string;
  takeoffRows: readonly ExportPreviewTakeoffRow[];
  productRows: readonly ExportPreviewBomRowDisplay[];
  apartmentSchedules: readonly ExportPreviewApartmentScheduleRow[];
  scheduleEntries: readonly ExportPreviewScheduleEntry[];
  noteRows: readonly ExportPreviewNoteRow[];
  rfiRows: readonly ExportPreviewRfiRow[];
  cablingRows: readonly ExportPreviewCablingRow[];
  detailRows: readonly ExportPreviewDetailRow[];
  specificationSections: readonly ExportPreviewSpecificationSection[];
  edgeExtensions: readonly ExportPreviewEdgeExtension[];
  gaps: readonly string[];
}

const textRows = (rows: readonly string[]): string[] =>
  rows.length > 0
    ? [...rows]
    : ["- Not available from the current data for this selected scope."];

interface ClientPackageIdentity {
  projectLabel: string;
  projectId: string | undefined;
  selectedScopeLabel: string;
  scopeId: string | undefined;
  exportBundleId: string | undefined;
  outputBundleHash: string | undefined;
  generatedAt: string | undefined;
}

const clientPackagePartHeader = (
  partName: string,
  input: ClientPackageIdentity
): string[] =>
  [
    partName,
    `Project: ${input.projectLabel}`,
    input.projectId ? `Project id: ${input.projectId}` : undefined,
    `Scope: ${input.selectedScopeLabel}`,
    input.scopeId ? `Scope id: ${input.scopeId}` : undefined,
    input.exportBundleId ? `Export reference: ${input.exportBundleId}` : undefined,
    input.outputBundleHash
      ? `Output bundle hash: ${input.outputBundleHash}`
      : undefined,
    input.generatedAt ? `Generated at: ${input.generatedAt}` : undefined
  ].filter((line): line is string => Boolean(line));

export const buildClientPackageText = ({
  projectLabel,
  projectId,
  selectedScopeLabel,
  scopeId,
  exportBundleId,
  outputBundleHash,
  generatedAt,
  takeoffRows: selectedTakeoffs,
  productRows,
  apartmentSchedules: selectedSchedules,
  scheduleEntries: selectedScheduleEntries,
  noteRows: _selectedNoteRows,
  rfiRows: _selectedRfis,
  cablingRows: selectedCabling,
  detailRows: selectedDetails,
  specificationSections: selectedSpecificationSections,
  edgeExtensions: selectedEdgeExtensions,
  gaps
}: ExportClientPackageInput): string => {
  const identity = {
    projectLabel,
    projectId,
    selectedScopeLabel,
    scopeId,
    exportBundleId,
    outputBundleHash,
    generatedAt
  };
  const scheduleLines = [
    "",
    ...clientPackagePartHeader("Clarifications, exclusions and assumptions", identity),
    ...(selectedScheduleEntries.length > 0
      ? selectedScheduleEntries.map(
          (entry) =>
            `- ${entry.kindLabel} / ${entry.categoryLabel} / ${entry.affectedLabel}: ${entry.bodyText} Evidence: ${entry.evidenceSummary}. Provenance: ${entry.provenanceLabel}.`
        )
      : [
          "- Clarifications: none recorded.",
          "- Exclusions: none recorded.",
          "- Assumptions: none recorded."
        ])
  ];
  const lines = [
    `${projectLabel} controls specification`,
    "",
    ...clientPackagePartHeader("Scope", identity),
    `Selected scope: ${selectedScopeLabel}`,
    ...gaps
      .filter(
        (gap) =>
          !gap.toLowerCase().includes("rfi") &&
          !gap.toLowerCase().includes("project note")
      )
      .map((gap) => `- ${humaniseExportText(gap)}`),
    "",
    ...clientPackagePartHeader("Take-offs", identity),
    ...textRows(
      selectedTakeoffs.map(
        (row) =>
          `- ${row.apartmentTypeLabel}: ${countLabel(row.quantity, "home")}, ${row.bedsLabel}, ${row.fcuCountLabel}. What the AI used: ${row.aiWhat} Why: ${row.aiWhy}`
      )
    ),
    "",
    ...clientPackagePartHeader("Specifications", identity),
    ...textRows(
      selectedSpecificationSections.map(
        (section) => `- ${section.heading}: ${section.body} Why: ${section.aiWhy}`
      )
    ),
    "",
    ...clientPackagePartHeader("Products", identity),
    ...textRows(
      productRows.map((row) => {
        const productLabel =
          row.productPartNo && !row.name.includes(row.productPartNo)
            ? `${row.productPartNo} | ${row.name}`
            : row.name;
        const provisionScopeBasis = storedBasisSummaryForBomRow(
          row,
          "provisionScopeBasis"
        );
        const installIncludedBasis = storedBasisSummaryForBomRow(
          row,
          "installIncludedBasis"
        );

        return `- ${productLabel}: ${row.projectQuantity} required, ${row.supplySourceLabel}. What the AI selected: ${row.description}. Why: ${row.why}. Provision scope basis: ${provisionScopeBasis}. Install included basis: ${installIncludedBasis}.`;
      })
    ),
    "",
    ...clientPackagePartHeader("Apartment schedules", identity),
    ...textRows(
      selectedSchedules.map(
        (row) =>
          `- ${row.apartmentTypeLabel}: ${countLabel(row.homes, "home")}; per home ${row.controllersPerHome} controller${row.controllersPerHome === 1 ? "" : "s"}, ${row.devicesPerHome} room device${row.devicesPerHome === 1 ? "" : "s"}, ${row.infrastructurePerHome} infrastructure item${row.infrastructurePerHome === 1 ? "" : "s"}. Why: ${row.aiWhy}`
      )
    ),
    ...scheduleLines,
    "",
    ...clientPackagePartHeader("Cabling", identity),
    ...textRows(
      selectedCabling
        .slice(0, 24)
        .map(
          (row) =>
            `- ${row.apartmentTypeLabel}: ${row.method} (${row.protocolLabel}); applies to ${row.appliesTo}. Why: ${row.aiWhy}`
        )
    ),
    "",
    ...clientPackagePartHeader("Details", identity),
    ...textRows(
      selectedDetails
        .slice(0, 24)
        .map(
          (row) =>
            `- ${row.categoryLabel} / ${row.affectedLabel}: ${row.title}. ${row.detail} Why: ${row.aiWhy}`
        )
    ),
    "",
    ...clientPackagePartHeader("Edge and integration", identity),
    ...textRows(
      selectedEdgeExtensions.map(
        (extension) =>
          `- ${extension.title}: ${extension.provisionUnit}; ${extension.inputPolicy}; ${extension.connectivity}.`
      )
    )
  ];

  return `${lines.join("\n")}\n`;
};

const csvCell = (value: string | number): string =>
  `"${String(value).replace(/"/g, '""')}"`;

export const storedBasisLabel = (value: string | undefined): string =>
  value ?? "not specified";

export const storedBasisSummaryForBomRow = (
  row: ExportPreviewBomRowDisplay,
  key: "provisionScopeBasis" | "installIncludedBasis"
): string => {
  const values = uniqueStrings(
    [row[key], ...row.detailRows.map((detail) => detail[key])].filter(
      (value): value is string => Boolean(value)
    )
  );

  return storedBasisLabel(values.length > 0 ? values.join(" | ") : undefined);
};

export const storedProvisionScopeLabel = (
  value: ExportPreviewBomRowDetail["provisionScope"]
): string => value ?? "not specified";

export const storedInstallIncludedLabel = (
  value: ExportPreviewBomRowDetail["installIncluded"]
): string => (value === undefined ? "not specified" : String(value));

const displayProvisionScopeLabel = (
  value: ExportPreviewBomRowDetail["provisionScope"]
): string => {
  if (value === "apartment_type") {
    return "Apartment type";
  }

  return value === "project" ? "Project" : "Not specified";
};

const displayInstallIncludedLabel = (
  value: ExportPreviewBomRowDetail["installIncluded"]
): string =>
  value === undefined ? "Not specified" : value ? "Included" : "Not included";

export const buildControlsBomCsv = (
  rows: readonly ExportPreviewBomRowDisplay[],
  exportNote?: string | null
): string =>
  [
    ...(exportNote ? [["Export note", exportNote], []] : []),
    [
      "Apartment type",
      "Item class",
      "Item kind",
      "Product code",
      "Description",
      "Supply source",
      "Provision scope",
      "Install included",
      "Qty per type",
      "Type qty",
      "Project qty",
      "Source row count",
      "Apartment details",
      "Why",
      "How",
      "Install notes",
      "Risk refs"
    ],
    ...rows
      .filter((row) => !isCablingDisplayRow(row))
      .map((row) => [
        row.apartmentTypeLabel,
        labelise(row.itemClass),
        row.itemKindLabel,
        row.productPartNo ?? "",
        row.description,
        row.supplySourceLabel,
        displayProvisionScopeLabel(row.provisionScope),
        displayInstallIncludedLabel(row.installIncluded),
        row.quantityPerApartmentType,
        row.apartmentTypeQuantity,
        row.projectQuantity,
        row.sourceRowCount,
        row.detailRows
          .map((detail) => `${detail.apartmentTypeLabel}: ${detail.projectQuantity}`)
          .join(" | "),
        row.why,
        row.how,
        row.installNotes.join(" | "),
        row.riskRefs.join(" | ")
      ])
  ]
    .map((line) => line.map(csvCell).join(","))
    .join("\r\n");

export const projectFixtureToExportPreviewView = (
  fixture: ProjectFixtureForDisplay
): ExportPreviewDisplayViewModel => {
  const primaryBundle = fixture.outputBundle.exportBundles.find(
    (bundle) => bundle.status === "ready"
  );
  const selectedBundle = primaryBundle ?? fixture.outputBundle.exportBundles[0];
  const blockingRiskIds = fixture.risks
    .filter((risk) => risk.blocksExport)
    .map((risk) => risk.id);
  const artifactIds =
    selectedBundle?.artifacts.map((artifact) => artifact.id) ??
    fixture.outputBundle.exportBundles.flatMap((bundle) =>
      bundle.artifacts.map((artifact) => artifact.id)
    );
  const rows = bomRows(fixture);
  const reqs = requirements(fixture);
  const notes = installNotes(rows);
  const mappings = mappingRows(fixture);
  const edges = edgeExtensions(fixture, rows);
  const takeoffs = takeoffRows(fixture);
  const schedules = apartmentSchedules(fixture, rows);
  const acceptedScheduleEntries = scheduleEntries(fixture);
  const rfis = rfiRows(fixture);
  const cabling = cablingRowsFromMappings(mappings);
  const details = detailRows(fixture, notes);
  const projectNotes = noteRows(fixture);
  const specification = specificationSections({
    requirements: reqs,
    rows,
    cabling,
    edges
  });
  const commissioningTaskCount = fixture.outputBundle.outputGraphs.reduce(
    (sum, graph) => sum + graph.commissioningTasks.length,
    0
  );
  const core = ExportPreviewViewSchema.parse({
    projectId: fixture.project.projectId,
    exportBundleId: selectedBundle?.id ?? fixture.outputBundle.id,
    status: fixture.outputBundle.status,
    artifactIds,
    blockingRiskIds,
    updatedAt: fixture.fixture.generatedAt
  });

  return {
    ...core,
    generatedFrom: fixture.fixture.generatedFrom,
    partialScope: fixture.fixture.partialScope ?? null,
    draftScope: fixture.fixture.draftScope ?? null,
    scopeOptions: scopeOptions(fixture),
    includeOptions,
    bomRows: rows,
    requirements: reqs,
    installNotes: notes,
    takeoffRows: takeoffs,
    apartmentSchedules: schedules,
    rfiRows: rfis,
    cablingRows: cabling,
    detailRows: details,
    specificationSections: specification,
    scheduleEntries: acceptedScheduleEntries,
    noteRows: projectNotes,
    deferredScheduleExtensionPoints: deferredScheduleExtensionPoints(
      acceptedScheduleEntries.length > 0,
      projectNotes.length > 0
    ),
    mappingRows: mappings,
    edgeExtensions: edges,
    stats: {
      bomLineCount: rows.length,
      totalControlsQuantity: rows.reduce((sum, row) => sum + row.projectQuantity, 0),
      takeoffRowCount: takeoffs.length,
      apartmentScheduleCount: schedules.length,
      rfiCount: rfis.length,
      cablingRowCount: cabling.length,
      detailCount: details.length,
      specificationSectionCount: specification.length,
      scheduleEntryCount: acceptedScheduleEntries.length,
      noteCount: projectNotes.length,
      requirementCount: fixture.project.requirements.length,
      installNoteCount: notes.length,
      mappingRowCount: mappings.length,
      commissioningTaskCount,
      edgeExtensionCount: edges.length
    },
    supplySourceBreakdown: supplySourceBreakdown(rows),
    outputBundleHash: fixture.outputBundle.hash,
    openRiskIds: fixture.risks
      .filter((risk) => risk.status === "open")
      .map((risk) => risk.id),
    dataGaps: dataGapsFor({
      fixture,
      rows,
      installNotes: notes,
      projectNotes,
      takeoffs,
      schedules,
      scheduleEntries: acceptedScheduleEntries,
      rfis,
      mappings,
      cabling,
      details,
      specification,
      edges
    })
  };
};
